import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import Icon from "components/atoms/Icon";

const Panel = styled.section`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    border-right: 1px solid #d3dde8;
    overflow: hidden;
    background: #f7fafc;
`;

const Shell = styled.div`
    display: ${(props) => (props.$collapsed ? "none" : "flex")};
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    width: 100%;
    height: 100%;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid #dde6f0;
    background: #f6f9fc;
`;

const HeaderInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const HeaderText = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 800;
    color: #132238;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Subtitle = styled.div`
    font-size: 12px;
    color: #6d7f96;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const RailBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 12px;
    overflow: auto;
    background: #fbfcfe;
`;

const TreeBody = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
    padding: 8px 6px 10px 6px;
    overflow: auto;
    background: #fbfcfe;
`;

const TreeDropOverlay = styled.div`
    position: absolute;
    inset: 10px;
    display: ${(props) => (props.$visible ? "flex" : "none")};
    align-items: center;
    justify-content: center;
    border: 2px dashed #0f8f6f;
    border-radius: 12px;
    background: rgba(228, 250, 241, 0.94);
    color: #0d5f4b;
    font-size: 12px;
    font-weight: 700;
    pointer-events: none;
    z-index: 2;
`;

const SubPanel = styled.section`
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: #fbfcfe;
    overflow: hidden;
`;

const SubPanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 42px;
    padding: 10px 14px;
    border-bottom: 1px solid #dce5ee;
    background: #f3f7fb;
`;

const SubPanelActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: nowrap;
    min-width: 0;
`;

const CompactTextButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 26px;
    padding: 0 8px;
    border: 1px solid #d5dee8;
    border-radius: 6px;
    background: #ffffff;
    color: #32485e;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #eef3f7;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const OutlineSection = styled.div`
    display: flex;
    flex-direction: column;
    background: #eef3f7;
    overflow: hidden;
`;

const OutlineResizeHandle = styled.div`
    position: relative;
    height: 10px;
    flex: 0 0 10px;
    border-top: 1px solid #d7e1eb;
    border-bottom: 1px solid #d7e1eb;
    background: #e5edf5;
    cursor: row-resize;

    &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        width: 54px;
        height: 3px;
        border-radius: 999px;
        background: #bcc9d8;
        transform: translate(-50%, -50%);
    }
`;

const OutlineHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    min-height: 42px;
    padding: 10px 14px;
    border-bottom: 1px solid #dce5ee;
    background: #f3f7fb;
`;

const OutlineBody = styled.div`
    flex: 1;
    min-height: 0;
    padding: 10px 16px 14px 16px;
    overflow: auto;
`;

const OutlineList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const OutlineItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    border: 1px solid ${(props) => (props.$active ? "#b9d4ff" : props.$ancestor ? "#e2e8f0" : "transparent")};
    border-radius: 6px;
    background: ${(props) => (props.$active ? "#e7f1ff" : props.$ancestor ? "#f8fbff" : "transparent")};
    font-size: 11px;
    color: #2c435d;
    text-align: left;
    cursor: pointer;
`;

const OutlineRow = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    padding-left: ${(props) => `${props.$level * 12}px`};

    &::before {
        content: "";
        position: absolute;
        top: -6px;
        bottom: -6px;
        left: ${(props) => `${props.$level * 12 + 6}px`};
        width: 1px;
        background: ${(props) => (props.$showGuide ? "#cfd9e4" : "transparent")};
    }
`;

const OutlineToggle = styled.button`
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6c8096;
    cursor: pointer;
`;

const OutlineSpacer = styled.span`
    position: relative;
    z-index: 1;
    width: 14px;
    height: 14px;
    flex: 0 0 14px;
`;

const OutlineTitle = styled.span`
    display: block;
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const OutlineMeta = styled.span`
    flex: 0 0 auto;
    color: #73879b;
    font-size: 10px;
`;

const Placeholder = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 32px;
    color: #4d6178;
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 0;
    height: 30px;
    padding: 0 9px;
    border: 1px solid #d2dce8;
    border-radius: 8px;
    background: #ffffff;
    color: #1f3347;
    font-size: 13px;
    outline: none;
`;

const SearchSummary = styled.div`
    font-size: 11px;
    color: #5f7489;
`;

const SearchResultList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SearchResultItem = styled.button`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #dde7f1;
    border-radius: 8px;
    background: #ffffff;
    text-align: left;
    cursor: pointer;
`;

const SearchResultMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 10px;
    color: #5f7489;
`;

const SearchResultPath = styled.div`
    font-size: 11px;
    font-weight: 700;
    color: #1e344a;
`;

const SearchResultSnippet = styled.div`
    font-size: 11px;
    line-height: 1.35;
    color: #344b60;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FilesShell = styled.div`
    display: grid;
    grid-template-rows: minmax(42px, 1fr) 10px ${(props) => `${props.$outlineHeight}px`};
    flex: 1;
    min-height: 0;
`;

function OutlineTree({
    items,
    expandedMap,
    toggleExpanded,
    activeOutlineLine,
    activeOutlineIds,
    onOutlineSelect,
    level = 0,
    parentHasGuide = false,
}) {
    return (
        <OutlineList>
            {items.map((item) => {
                const isExpanded = expandedMap[item.id] ?? true;
                const hasChildren = item.children?.length > 0;
                const isCurrent = activeOutlineIds?.[activeOutlineIds.length - 1] === item.id;
                const isAncestor = activeOutlineIds?.includes(item.id) && !isCurrent;

                return (
                    <React.Fragment key={item.id}>
                        <OutlineItem
                            $active={isCurrent}
                            $ancestor={isAncestor}
                            role="button"
                            tabIndex={0}
                            onClick={() => onOutlineSelect(item.line)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    onOutlineSelect(item.line);
                                }
                            }}
                        >
                            <OutlineRow $level={level} $showGuide={level > 0 && !hasChildren && parentHasGuide}>
                                {hasChildren ? (
                                    <OutlineToggle
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggleExpanded(item.id);
                                        }}
                                    >
                                        <Icon
                                            name={isExpanded ? "arrowDown" : "arrowRight"}
                                            size={"10px"}
                                            color={"#6c8096"}
                                        />
                                    </OutlineToggle>
                                ) : (
                                    <OutlineSpacer />
                                )}
                                <OutlineTitle>{item.title}</OutlineTitle>
                                <OutlineMeta>L{item.line}</OutlineMeta>
                            </OutlineRow>
                        </OutlineItem>
                        {hasChildren && isExpanded ? (
                            <OutlineTree
                                items={item.children}
                                expandedMap={expandedMap}
                                toggleExpanded={toggleExpanded}
                                activeOutlineLine={activeOutlineLine}
                                activeOutlineIds={activeOutlineIds}
                                onOutlineSelect={onOutlineSelect}
                                level={level + 1}
                                parentHasGuide={true}
                            />
                        ) : null}
                    </React.Fragment>
                );
            })}
        </OutlineList>
    );
}

function ManuscriptLeftPanel({
    activePanel,
    roots,
    renderTreeNode,
    selectedNode,
    createNode,
    fileInputRef,
    uploadBinary,
    searchQuery,
    setSearchQuery,
    onSearchSubmit,
    searchLoading,
    searchResults,
    onSearchResultSelect,
    outlineItems,
    outlineEnabled,
    activeOutlineLine,
    activeOutlineIds,
    onOutlineSelect,
    isCollapsed,
}) {
    const [outlineHeight, setOutlineHeight] = useState(220);
    const [outlineExpandedMap, setOutlineExpandedMap] = useState({});
    const [isFileDragging, setIsFileDragging] = useState(false);
    const outlineDragRef = useRef(null);
    const fileDragDepthRef = useRef(0);
    const filesShellRef = useRef(null);

    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!outlineDragRef.current) return;
            const { startY, startHeight } = outlineDragRef.current;
            const delta = startY - event.clientY;
            const shellHeight = filesShellRef.current?.clientHeight || 0;
            const maxOutlineHeight = Math.max(42, shellHeight - 52);
            const nextHeight = Math.min(maxOutlineHeight, Math.max(42, startHeight + delta));
            setOutlineHeight(nextHeight);
        };
        const handleMouseUp = () => {
            outlineDragRef.current = null;
        };
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    const toggleOutlineExpanded = (id) => {
        setOutlineExpandedMap((prev) => ({
            ...prev,
            [id]: !(prev[id] ?? true),
        }));
    };

    const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");

    const handleFileDragEnter = (event) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        event.stopPropagation();
        fileDragDepthRef.current += 1;
        setIsFileDragging(true);
    };

    const handleFileDragOver = (event) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = "copy";
        if (!isFileDragging) {
            setIsFileDragging(true);
        }
    };

    const handleFileDragLeave = (event) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        event.stopPropagation();
        fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
        if (fileDragDepthRef.current === 0) {
            setIsFileDragging(false);
        }
    };

    const handleFileDrop = async (event) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        event.stopPropagation();
        fileDragDepthRef.current = 0;
        setIsFileDragging(false);
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length) {
            await uploadBinary(files);
        }
    };

    return (
        <Panel>
            <Shell $collapsed={isCollapsed}>
                {activePanel === "files" ? (
                    <FilesShell ref={filesShellRef} $outlineHeight={outlineHeight}>
                        <SubPanel>
                            <SubPanelHeader>
                                <HeaderInfo>
                                    <Icon name={"folder"} size={"14px"} color={"#0f4c81"} />
                                    <HeaderText>
                                        <Title>파일 트리</Title>
                                    </HeaderText>
                                </HeaderInfo>
                                <SubPanelActions>
                                    <CompactTextButton type="button" onClick={() => createNode("folder")}>
                                        새 폴더
                                    </CompactTextButton>
                                    <CompactTextButton type="button" onClick={() => createNode("text")}>
                                        새 파일
                                    </CompactTextButton>
                                    <CompactTextButton
                                        type="button"
                                        onClick={() => {
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                                fileInputRef.current.click();
                                            }
                                        }}
                                    >
                                        업로드
                                    </CompactTextButton>
                                    <input ref={fileInputRef} type="file" hidden multiple onChange={uploadBinary} />
                                </SubPanelActions>
                            </SubPanelHeader>
                            <TreeBody
                                onDragEnter={handleFileDragEnter}
                                onDragOver={handleFileDragOver}
                                onDragLeave={handleFileDragLeave}
                                onDrop={handleFileDrop}
                            >
                                <TreeDropOverlay $visible={isFileDragging}>파일을 여기에 놓아 업로드</TreeDropOverlay>
                                {roots.length ? (
                                    roots.map((node) => renderTreeNode(node))
                                ) : (
                                    <Placeholder>파일이 없습니다.</Placeholder>
                                )}
                            </TreeBody>
                        </SubPanel>
                        <OutlineResizeHandle
                            onMouseDown={(event) => {
                                outlineDragRef.current = { startY: event.clientY, startHeight: outlineHeight };
                            }}
                        />
                        <OutlineSection>
                            <OutlineHeader>
                                <HeaderInfo>
                                    <Icon name={"memo"} size={"14px"} color={"#0f4c81"} />
                                    <HeaderText>
                                        <Title>파일 개요</Title>
                                    </HeaderText>
                                </HeaderInfo>
                            </OutlineHeader>
                            <OutlineBody>
                                <OutlineList>
                                    {outlineEnabled && outlineItems.length ? (
                                        <OutlineTree
                                            items={outlineItems}
                                            expandedMap={outlineExpandedMap}
                                            toggleExpanded={toggleOutlineExpanded}
                                            activeOutlineLine={activeOutlineLine}
                                            activeOutlineIds={activeOutlineIds}
                                            onOutlineSelect={onOutlineSelect}
                                        />
                                    ) : outlineEnabled ? (
                                        <MetaValue>문서에서 섹션 구조를 찾지 못했습니다.</MetaValue>
                                    ) : (
                                        <MetaValue>아직 개요를 표시할 문서가 없습니다.</MetaValue>
                                    )}
                                </OutlineList>
                            </OutlineBody>
                        </OutlineSection>
                    </FilesShell>
                ) : null}

                {activePanel === "search" ? (
                    <>
                        <PanelHeader>
                            <HeaderInfo>
                                <Icon name={"search"} size={"16px"} color={"#0f8f6f"} />
                                <HeaderText>
                                    <Title>검색</Title>
                                    <Subtitle>전체 파일 검색</Subtitle>
                                </HeaderText>
                            </HeaderInfo>
                        </PanelHeader>
                        <RailBody>
                            <Stack>
                                <SubPanel
                                    style={{
                                        padding: "12px",
                                        gap: "8px",
                                        border: "1px dashed #c9d6e5",
                                        borderRadius: "14px",
                                        background: "#ffffff",
                                    }}
                                >
                                    <SearchForm
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                            onSearchSubmit?.();
                                        }}
                                    >
                                        <SearchInput
                                            value={searchQuery}
                                            onChange={(event) => setSearchQuery(event.target.value)}
                                            placeholder="파일명 또는 본문 검색"
                                        />
                                        <CompactTextButton
                                            type="submit"
                                            disabled={searchLoading || !searchQuery.trim()}
                                        >
                                            검색
                                        </CompactTextButton>
                                    </SearchForm>
                                    <SearchSummary>
                                        {searchLoading
                                            ? "검색 중..."
                                            : searchResults.length
                                              ? `${searchResults.length}개 결과`
                                              : "검색 결과가 없습니다."}
                                    </SearchSummary>
                                </SubPanel>
                                <SearchResultList>
                                    {searchResults.map((result) => (
                                        <SearchResultItem
                                            key={result.id}
                                            onClick={() => onSearchResultSelect?.(result)}
                                        >
                                            <SearchResultPath>{result.path}</SearchResultPath>
                                            <SearchResultMeta>
                                                <span>{result.kind === "content" ? "본문" : "파일명"}</span>
                                                {result.line ? <span>Ln {result.line}</span> : null}
                                            </SearchResultMeta>
                                            {result.snippet ? (
                                                <SearchResultSnippet>{result.snippet}</SearchResultSnippet>
                                            ) : null}
                                        </SearchResultItem>
                                    ))}
                                </SearchResultList>
                            </Stack>
                        </RailBody>
                    </>
                ) : null}
            </Shell>
        </Panel>
    );
}

export default ManuscriptLeftPanel;
